export const CREDITS_BANNER_STORAGE_KEY = "creditsBanner";
export const CREDITS_BANNER_EVENT = "credits-banner-updated";
export const CREDIT_ALERT_MESSAGE_TYPE = "CREDIT_ALERT";
export const CREDIT_ALERT_TYPE_BANNER_UPDATED = "BANNER_UPDATED";
export const CREDIT_ALERT_TYPE_CREDITS_EXHAUSTED = "CREDITS_EXHAUSTED";
export const CREDIT_ALERT_TYPE_CREDITS_LOW_90 = "CREDITS_LOW_90";
export const CREDIT_ALERT_TYPE_CREDITS_LOW_75 = "CREDITS_LOW_75";
export const CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON = "TRIAL_ENDING_SOON";
export const CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON =
  "SUBSCRIPTION_EXPIRING_SOON";
export const CREDIT_ALERT_TYPE_PAYMENT_FAILED = "PAYMENT_FAILED";
export const CREDIT_ALERT_TYPE_PILOT_EXPIRED = "PILOT_EXPIRED";
export const CREDIT_ALERT_TYPE_PLAN_RENEWED = "PLAN_RENEWED";
export const CREDIT_ALERT_TYPE_PLAN_ACTIVATED = "PLAN_ACTIVATED";
export const CREDIT_ALERT_TYPE_CREDITS_ADDED = "CREDITS_ADDED";
export const CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED =
  "SUBSCRIPTION_EXPIRED";
export const CREDITS_BANNER_LEVEL_FAILURE = "failure";
export const CREDITS_BANNER_LEVEL_WARNING = "warning";
export const CREDITS_BANNER_LEVEL_SUCCESS = "success";
export const CREDITS_BANNER_LEVEL_INFO = "info";
export const CREDITS_BANNER_PREVIEW_QUERY_KEY = "bannerPreview";

const FAILURE_ALERT_TYPES = [
  CREDIT_ALERT_TYPE_PAYMENT_FAILED,
  CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED,
  CREDIT_ALERT_TYPE_PILOT_EXPIRED,
];

const WARNING_ALERT_TYPES = [
  CREDIT_ALERT_TYPE_CREDITS_EXHAUSTED,
  CREDIT_ALERT_TYPE_CREDITS_LOW_90,
  CREDIT_ALERT_TYPE_CREDITS_LOW_75,
  CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON,
  CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON,
];

const SUCCESS_ALERT_TYPES = [
  CREDIT_ALERT_TYPE_PLAN_RENEWED,
  CREDIT_ALERT_TYPE_PLAN_ACTIVATED,
  CREDIT_ALERT_TYPE_CREDITS_ADDED,
];

export const isCreditAlertMessage = (message) =>
  message?.type === CREDIT_ALERT_MESSAGE_TYPE;

export const isCreditsBannerUpdateMessage = (message) =>
  isCreditAlertMessage(message) &&
  message?.alertType === CREDIT_ALERT_TYPE_BANNER_UPDATED;

export const getSocketAlertPayload = (message) => {
  if (!message || typeof message !== "object") {
    return null;
  }

  if (message?.data?.banner && typeof message.data.banner === "object") {
    return message.data.banner;
  }

  if (message?.payload && typeof message.payload === "object") {
    return message.payload;
  }

  if (message?.data && typeof message.data === "object") {
    return message.data;
  }

  return null;
};

const getBannerLevel = (alertType, payload) => {
  if (payload?.level) {
    return String(payload.level).toLowerCase();
  }

  if (payload?.error === true) {
    return CREDITS_BANNER_LEVEL_FAILURE;
  }

  if (FAILURE_ALERT_TYPES.includes(alertType)) {
    return CREDITS_BANNER_LEVEL_FAILURE;
  }

  if (WARNING_ALERT_TYPES.includes(alertType)) {
    return CREDITS_BANNER_LEVEL_WARNING;
  }

  if (SUCCESS_ALERT_TYPES.includes(alertType)) {
    return CREDITS_BANNER_LEVEL_SUCCESS;
  }

  return CREDITS_BANNER_LEVEL_INFO;
};

const getButtonText = (alertType, payload) => {
  if (payload?.buttonText || payload?.ctaText || payload?.actionText) {
    return payload.buttonText || payload.ctaText || payload.actionText;
  }

  switch (alertType) {
    case CREDIT_ALERT_TYPE_CREDITS_EXHAUSTED:
    case CREDIT_ALERT_TYPE_CREDITS_LOW_90:
    case CREDIT_ALERT_TYPE_CREDITS_LOW_75:
      return "Buy Credits";
    case CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON:
      return "Upgrade Plan";
    case CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON:
    case CREDIT_ALERT_TYPE_PAYMENT_FAILED:
      return "Manage Subscription";
    case CREDIT_ALERT_TYPE_PILOT_EXPIRED:
      return "Upgrade Plan";
    case CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED:
      return "Buy Credits";
    default:
      return "";
  }
};

export const createCreditsBannerFromSocketAlert = (message) => {
  if (!isCreditAlertMessage(message)) {
    return null;
  }

  const payload = getSocketAlertPayload(message) || {};
  const alertType = message?.alertType || payload?.type || "";

  if (!alertType) {
    return null;
  }

  if (alertType === CREDIT_ALERT_TYPE_BANNER_UPDATED && payload?.banner) {
    return payload.banner;
  }

  if (alertType === CREDIT_ALERT_TYPE_BANNER_UPDATED) {
    return payload;
  }

  return {
    type: alertType,
    level: getBannerLevel(alertType, payload),
    message: payload?.message || payload?.title || "",
    detail: payload?.detail || payload?.description || payload?.body || "",
    buttonText: getButtonText(alertType, payload),
    ...payload,
  };
};

export const getCreditsBannerPreviewFromSearch = (
  search = window.location.search
) => {
  const params = new URLSearchParams(search || "");
  const previewValue = String(
    params.get(CREDITS_BANNER_PREVIEW_QUERY_KEY) || ""
  )
    .trim()
    .toLowerCase();

  if (!previewValue) {
    return null;
  }

  switch (previewValue) {
    case "warning":
      return {
        type: CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON,
        level: CREDITS_BANNER_LEVEL_WARNING,
        message: "Warning Banner Preview",
        detail: "Testing the warning banner icon and button styling.",
        buttonText: "Upgrade Plan",
      };
    case "failure":
      return {
        type: CREDIT_ALERT_TYPE_PAYMENT_FAILED,
        level: CREDITS_BANNER_LEVEL_FAILURE,
        message: "Failure Banner Preview",
        detail: "Testing the failure banner icon and button styling.",
        buttonText: "Manage Subscription",
      };
    case "success":
      return {
        type: CREDIT_ALERT_TYPE_PLAN_RENEWED,
        level: CREDITS_BANNER_LEVEL_SUCCESS,
        message: "Success Banner Preview",
        detail: "Testing the success banner icon styling.",
        buttonText: "",
      };
    case "info":
      return {
        type: CREDIT_ALERT_TYPE_PLAN_ACTIVATED,
        level: CREDITS_BANNER_LEVEL_INFO,
        message: "Info Banner Preview",
        detail: "Testing the info banner icon styling.",
        buttonText: "",
      };
    default:
      return null;
  }
};
